import React, { useRef, useState, useEffect, useCallback } from 'react'
import {
    View,
    Text,
    StyleSheet,
    ImageBackground,
    Dimensions,
    Animated,
    Easing,
    TouchableOpacity,
    ActivityIndicator,
    Platform,
    Image,
    AppState,
    useWindowDimensions,
} from 'react-native'
import {
    isAndroid,
    isWeb,
    isWeb as isWebPlatform,
} from '../../../utils/platformUtils'
// NOTE: expo-av Video component is deprecated in favor of expo-video.
// Migration is planned for a future release. Current implementation is stable.
import { Video, ResizeMode, Audio, VideoFullscreenUpdate } from 'expo-av'
import { useFocusEffect, NavigationContext } from '@react-navigation/native'
import mediaSingleton from '../../../utils/mediaSingleton'
import styles from './expo-av-Styles'
import * as ScreenOrientation from 'expo-screen-orientation'
import { useKeepAwake } from 'expo-keep-awake'
import { podcastBackground } from '../../../../themeConstants'
import { Ionicons, Feather } from '@expo/vector-icons'
import { searchFile } from '../Components/ImageWIthZoom'
import SizeCache from '../../../utils/sizeCache'
import { useDimensionsStore } from '@/redux/zustand/useDimensionStore'
import { NoConnectionVideo, useNoConnectionVideoModal } from './NoConnectionVideo'

// Cache global de ratios por URI para evitar "saltos" cuando un item es reciclado y re-montado
const RATIO_CACHE = new Map()

function VideoPlayerAV({
    uri,
    onVideoRef = () => {},
    autoplay,
    post_id,
    isPodcast,
    contentSeen,
    path,
    isVisible,
    containerWidth,
    initialRatio = undefined,
    onReady,
    onPlaybackStatusUpdateExternal,
    onNaturalSize,
    waitForNaturalSize = false,
    controlsMode = 'native',
    allowAutoplayAfterUserGesture = false,
    startMuted = false,
    externalShouldPlay = false,
    onUserPlayIntent = () => {},
    onFullscreenChange = () => {},
    onVideoEnd = () => {},
    onVideoPress = () => {},
    onLongPress = () => {},
    onDoubleTap = () => {},
    // Si true, el overlay de "Volver a ver" lo maneja el padre (MediaComponent),
    // y este componente NO debe mostrar su overlay interno ni mantenerlo visible.
    manageReplayOverlayExternally = false,
    // Gate opcional: no disparar onReady hasta que haya al menos X ms reproducibles (prebuffer)
    prebufferGateMs = 0,
    isLearning = false,
    isFullScreen = false,
}) {
    const videoRef = useRef(null)
    const [status, setStatus] = useState({})
    // Listo para reproducir: true cuando el view nativo existe y reporta isLoaded/onReadyForDisplay
    const [isReadyToPlay, setIsReadyToPlay] = useState(false)
    const pendingAutoplayRef = useRef(false)
    const [wasPlaying, setWasPlaying] = useState(false)
    const [hasStarted, setHasStarted] = useState(false)
    const [lastPosition, setLastPosition] = useState(null)
    const instanceId = post_id
    const [video, setVideo] = useState(null)
    const [source, setSource] = useState(undefined)
    const [posterSource, setPosterSource] = useState(undefined)
    // ratio NATURAL detectado (w/h). displayRatio es el usado en layout para permitir transición controlada.
    // Modo intrínseco con fallback: queremos el ratio real, pero damos tamaño inicial para que cargue metadata
    const FALLBACK_RATIO = 16 / 9
    const [ratio, setRatio] = useState(null) // ratio natural (informativo, no afecta layout)
    // Intentar hidratar ratio desde SizeCache (persistente) antes de usar fallback; esto reduce flicker en remounts/paginación
    const initialDisplayRatio = (() => {
        try {
            // Priorizar ratio inicial provisto por el padre (derivado de imagen del response)
            const r0 = Number(initialRatio)
            if (isFinite(r0) && r0 > 0.2 && r0 < 4) return r0
            const cachedUrl = typeof uri === 'string' ? uri : null
            if (cachedUrl) {
                const rPersisted = SizeCache.getRatioForUrl?.(cachedUrl)
                if (
                    typeof rPersisted === 'number' &&
                    rPersisted > 0.2 &&
                    rPersisted < 4
                )
                    return rPersisted
            }
        } catch {}
        return FALLBACK_RATIO
    })()
    const [displayRatio, setDisplayRatio] = useState(initialDisplayRatio) // ratio aplicado (inicializar con cache o fallback)
    // Bloquear layout para toda la sesión con el primer ratio (meta/cache o fallback) para evitar pantallasos
    const lockLayoutRef = useRef(true)
    const [hasNaturalSize, setHasNaturalSize] = useState(false)
    const [showCustomControls, setShowCustomControls] = useState(false)
    const controlsOpacity = useRef(new Animated.Value(1)).current
    const [isMuted, setIsMuted] = useState(false) // Estado para mute en modo post
    const [isMutedMinimal, setIsMutedMinimal] = useState(!!startMuted) // Mute para modo minimal (learnings)
    const muteScale = useRef(new Animated.Value(1)).current
    const [muteHover, setMuteHover] = useState(false) // sólo aplicará en web
    // Debug de gestos/taps
    const DEBUG_GESTURES = false
    // Overlay de replays al finalizar
    const [showReplayOverlay, setShowReplayOverlay] = useState(false)

    const { width: deviceWidth, height: screenH } = useWindowDimensions()
    const endedRef = useRef(false)
    const onMutePressIn = () => {
        Animated.spring(muteScale, {
            toValue: 0.92,
            useNativeDriver: true,
            speed: 24,
            bounciness: 0,
        }).start()
    }
    const onMutePressOut = () => {
        Animated.spring(muteScale, {
            toValue: 1,
            useNativeDriver: true,
            speed: 24,
            bounciness: 0,
        }).start()
    }
    const controlsTimeoutRef = useRef(null)
    // Detección de doble-tap en capa base
    const lastTapRef = useRef(0)
    const singleTapTimeoutRef = useRef(null)
    // Evitar re-calcular ratio luego del primer naturalSize válido (previene "pantallasos")
    const ratioLockedRef = useRef(false)
    // Flag adicional para asegurar que applyNaturalSize se ejecute una sola vez
    const naturalSizeAppliedRef = useRef(false)
    // En web, permitir no renderizar el Video hasta tener naturalSize si así se solicita
    const [shouldRenderVideo, setShouldRenderVideo] = useState(() => {
        return isWebPlatform() ? !waitForNaturalSize : true
    })
    const progressBarRef = useRef(null)
    const inlineTrackRef = useRef({}) // { [index]: element }
    const inlineTrackWidthRef = useRef({}) // { [index]: element }

    // Gate de prebuffer: declarar ANTES de cualquier uso para evitar TDZ en Web
    const [notifiedReady, setNotifiedReady] = useState(false)
    const normalizedPrebufferGateMs = Math.max(0, Number(prebufferGateMs || 0))
    const maybeNotifyReadyFromStatus = useCallback(
        (s) => {
            if (notifiedReady) return
            // Si en Web el autoplay está deshabilitado por política (no hubo gesto),
            // no podemos esperar a buffers/reproducción: consideramos listo cuando el player está listo/mostrable.
            const webNoAutoplay = isWebPlatform() && !allowAutoplayAfterUserGesture
            const gateOff =
                normalizedPrebufferGateMs <= 0 ||
                (webNoAutoplay &&
                    (isReadyToPlay || hasNaturalSize || shouldRenderVideo))
            try {
                if (gateOff) {
                    setNotifiedReady(true)
                    typeof onReady === 'function' && onReady()
                    return
                }
                const st = s || status || {}
                const playable = Math.max(0, Number(st?.playableDurationMillis || 0))
                const pos = Math.max(0, Number(st?.positionMillis || 0))
                const isPlaying = !!st?.isPlaying
                if (isPlaying || pos > 0 || playable >= normalizedPrebufferGateMs) {
                    setNotifiedReady(true)
                    try {
                        typeof onReady === 'function' && onReady()
                    } catch {}
                }
            } catch {}
        },
        [
            notifiedReady,
            normalizedPrebufferGateMs,
            onReady,
            status,
            isReadyToPlay,
            hasNaturalSize,
            shouldRenderVideo,
            allowAutoplayAfterUserGesture,
        ],
    )
    // Validación de ratios extremos
    const MIN_VALID_RATIO = 0.2
    const MAX_VALID_RATIO = 4

    const [effectiveVisible, setEffectiveVisible] = useState(!!isVisible)

    const screenWidth = useDimensionsStore((state) => state.width)
    const screenHeight = useDimensionsStore((state) => state.height)

    // Persistir ratio inicial si viene del padre y aún no está cacheado
    useEffect(() => {
        try {
            const r0 = Number(initialRatio)
            if (typeof uri === 'string' && isFinite(r0) && r0 > 0.2 && r0 < 4) {
                const persisted = SizeCache.getRatioForUrl?.(uri)
                if (
                    !(
                        typeof persisted === 'number' &&
                        persisted > 0.2 &&
                        persisted < 4
                    )
                ) {
                    SizeCache.setRatioForUrl?.(uri, r0)
                }
            }
        } catch {}
    }, [uri, initialRatio])
    const visibilityTimerRef = useRef(null)
    const appStateRef = useRef(AppState.currentState)
    const wasPlayingBeforeBackgroundRef = useRef(false)

    const [inlinePlayback, setInlinePlayback] = useState({})

    const [videoLoaded, setVideoLoaded] = useState(false)
    const [videoError, setVideoError] = useState(false)
    const [retryKey, setRetryKey] = useState(0)

    const showOfflineModal = useNoConnectionVideoModal({
        videoError,
        videoLoaded,
        setVideoError,
        setRetryKey,
    })

    useEffect(() => {
        // Resetear flag cuando cambia el video
        naturalSizeAppliedRef.current = false

        // debug suprimido: props
        if (!uri) return
        const doFetch = async () => {
            if (!path || path.trim() === '') {
                setVideo(uri)
                setSource({ uri })
                // debug suprimido: remote uri
                // Poster remoto tentativo (mismo nombre con .jpeg)
                try {
                    const lower = (uri || '').toLowerCase()
                    if (
                        lower.endsWith('.mp4') ||
                        lower.endsWith('.mov') ||
                        lower.endsWith('.mkv')
                    ) {
                        const base = uri.replace(/\.(mp4|mov|mkv)$/i, '')
                        setPosterSource({ uri: `${base}.jpeg` })
                    }
                } catch {}
                return
            }
            try {
                const v = await searchFile(uri, path)
                setVideo(v)
                setSource(v ? { uri: v } : undefined)
                // debug suprimido: resolved video
                // Intentar poster local con misma base .jpeg o .jpg
                try {
                    const lower = (uri || '').toLowerCase()
                    if (
                        lower.endsWith('.mp4') ||
                        lower.endsWith('.mov') ||
                        lower.endsWith('.mkv')
                    ) {
                        const base = uri.replace(/\.(mp4|mov|mkv)$/i, '')
                        let poster = await searchFile(`${base}.jpeg`, path)
                        if (!poster) poster = await searchFile(`${base}.jpg`, path)
                        if (poster) setPosterSource({ uri: poster })
                    }
                } catch {}
            } catch (error) {
                // error silenciado: fetching video
            }
        }
        doFetch()
    }, [uri, path])

    // Si hay ratio cacheado para
    // esta URI, aplicarlo al montar para evitar cambios de tamaño
    useEffect(() => {
        const key = source?.uri
        if (!key) return
        // Order: memory RATIO_CACHE -> persistent SizeCache
        let cached = RATIO_CACHE.get(key)
        if (
            !(
                typeof cached === 'number' &&
                cached > MIN_VALID_RATIO &&
                cached < MAX_VALID_RATIO
            )
        ) {
            try {
                const rPersisted = SizeCache.getRatioForUrl?.(key)
                if (
                    typeof rPersisted === 'number' &&
                    rPersisted > MIN_VALID_RATIO &&
                    rPersisted < MAX_VALID_RATIO
                ) {
                    cached = rPersisted
                }
            } catch {}
        }
        if (
            typeof cached === 'number' &&
            cached > MIN_VALID_RATIO &&
            cached < MAX_VALID_RATIO
        ) {
            ratioLockedRef.current = true
            setRatio(cached)
            setDisplayRatio((prev) => (prev === FALLBACK_RATIO ? cached : prev))
            setHasNaturalSize(true)
            if (isWebPlatform() && waitForNaturalSize) setShouldRenderVideo(true)
            // Intentar notificar listo (respetando gate de prebuffer si aplica)
            maybeNotifyReadyFromStatus()
            try {
                typeof onNaturalSize === 'function' &&
                    onNaturalSize({
                        width: Math.round(deviceWidth * cached),
                        height: deviceWidth,
                        ratio: cached,
                    })
            } catch {}
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source?.uri])

    useEffect(() => {
        if (videoRef.current && post_id) {
            mediaSingleton.registerPlayer(post_id, videoRef.current)
        }
        // Limpieza al desmontar
        return () => {
            try {
                mediaSingleton.unregisterPlayer(post_id)
            } catch {}
        }
    }, [videoRef.current, post_id])

    useEffect(() => {
        if (onVideoRef) {
            onVideoRef(videoRef.current)
        }
    }, [videoRef.current])

    // Limpiar timeout al desmontar (controles personalizados)
    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current)
            }
            if (singleTapTimeoutRef.current) {
                clearTimeout(singleTapTimeoutRef.current)
                singleTapTimeoutRef.current = null
            }
        }
    }, [])

    // Debug opcional (silenciado por default)
    useEffect(() => {
        let debugFalse = false
        if (debugFalse && controlsMode === 'post') {
            console.log(
                '[VideoPlayerAV] Post mode - autoplay:',
                autoplay,
                'isVisible:',
                isVisible,
                'platform:',
                Platform.OS,
            )
            console.log(
                '[VideoPlayerAV] WEB_DISABLE_AUTOPLAY:',
                WEB_DISABLE_AUTOPLAY,
                'isWebPlatform:',
                isWebPlatform(),
            )
            console.log(
                '[VideoPlayerAV] shouldPlay will be:',
                !WEB_DISABLE_AUTOPLAY && autoplay && isVisible,
            )
        }
    }, [controlsMode, autoplay, isVisible])

    // Manejo del estado del video en modo post
    useEffect(() => {
        if (status?.didJustFinish && controlsMode === 'post') {
            // Notificar fin; el control de replay lo maneja onPlaybackStatusUpdate
            try {
                onVideoEnd()
            } catch {}
        }
    }, [status?.didJustFinish, controlsMode, onVideoEnd])

    const wasPlayingRef = useRef(false)

    // Hook auxiliar que evita crashear cuando no existe NavigationContainer (web simplificada)
    const navigationCtx = React.useContext(NavigationContext)
    const useOptionalFocusEffect = (cb) => {
        if (navigationCtx) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useFocusEffect(React.useCallback(cb, []))
        } else {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
                return cb()
            }, [])
        }
    }

    useOptionalFocusEffect(() => {
        if (wasPlayingRef.current && videoRef.current) {
            videoRef.current.playAsync().catch(() => {})
        }
        return () => {
            if (videoRef.current) {
                videoRef.current.pauseAsync().catch(() => {})
            }
        }
    })

    // Flags / estados específicos para WEB
    const WEB_DISABLE_AUTOPLAY = isWebPlatform() && !allowAutoplayAfterUserGesture
    const [userRequestedPlay, setUserRequestedPlay] = useState(false)
    const [canStartAfterBuffer, setCanStartAfterBuffer] =
        useState(!WEB_DISABLE_AUTOPLAY)
    const [bufferPercent, setBufferPercent] = useState(0)
    const [showPlayOverlay, setShowPlayOverlay] = useState(WEB_DISABLE_AUTOPLAY)
    const [waitingForBufferGate, setWaitingForBufferGate] = useState(false)
    const gatePercent = 0.2 // 20%

    const contentSeenTriggeredRef = useRef(false)

    const onPlaybackStatusUpdate = async (playbackStatus) => {
        setStatus(playbackStatus)
        wasPlayingRef.current = playbackStatus.isPlaying
        // debug suprimido: status

        // Marcar listo al cargar por primera vez
        if (playbackStatus?.isLoaded && !isReadyToPlay) {
            setIsReadyToPlay(true)
        }

        try {
            typeof onPlaybackStatusUpdateExternal === 'function' &&
                onPlaybackStatusUpdateExternal(playbackStatus)
        } catch {}

        // En cuanto el player reporta isLoaded, intentar notificar respetando el gate de prebuffer
        if (playbackStatus?.isLoaded) {
            maybeNotifyReadyFromStatus(playbackStatus)
        }

        // Ocultar póster solo cuando hay frames realmente disponibles para mostrar
        // Condiciones más estrictas para evitar flash negro en primera carga
        if (
            posterOverlayVisibleRef.current &&
            playbackStatus?.isLoaded &&
            !playbackStatus?.isBuffering &&
            // Caso 1: Video está reproduciendo activamente
            ((playbackStatus?.isPlaying === true &&
                (playbackStatus?.positionMillis || 0) > 100) ||
                // Caso 2: Video tiene posición significativa (>200ms) aunque esté pausado
                (playbackStatus?.positionMillis || 0) > 200)
        ) {
            hidePosterOverlay()
        }

        // Buffer percent (solo si plataforma lo expone)
        try {
            const playable = playbackStatus.playableDurationMillis
            const duration = playbackStatus.durationMillis
            if (playable && duration && duration > 0) {
                const pct = Math.min(1, playable / duration)
                setBufferPercent(pct)
                if (
                    WEB_DISABLE_AUTOPLAY &&
                    userRequestedPlay &&
                    !canStartAfterBuffer &&
                    pct >= gatePercent
                ) {
                    setCanStartAfterBuffer(true)
                }
            }
        } catch {}

        // Si comenzó a reproducir (por ejemplo, el padre disparó play tras rewatch), limpiar estado de fin y ocultar overlay interno
        if (playbackStatus?.isPlaying) {
            if (endedRef.current) endedRef.current = false
            if (showReplayOverlay) setShowReplayOverlay(false)
        }

        if (playbackStatus.didJustFinish) {
            setWasPlaying(false)
            setHasStarted(false)
            if (videoRef.current) {
                await videoRef.current.setPositionAsync(0)
                await videoRef.current.pauseAsync()
            }
            endedRef.current = true
            if (!manageReplayOverlayExternally) setShowReplayOverlay(true)

            if (
                path === 'learning' &&
                !isPodcast &&
                !contentSeenTriggeredRef.current
            ) {
                contentSeenTriggeredRef.current = true
                contentSeen()
            }

            // Al finalizar, no mostrar el overlay de play normal
            if (isWebPlatform()) setShowPlayOverlay(false)
            // Web: si el video estaba en fullscreen del navegador, salir para evitar quedar en negro
            if (isWebPlatform()) {
                try {
                    const doc = typeof document !== 'undefined' ? document : null
                    const fsEl =
                        doc &&
                        (doc.fullscreenElement ||
                            doc.webkitFullscreenElement ||
                            doc.mozFullScreenElement ||
                            doc.msFullscreenElement)
                    if (fsEl) {
                        if (doc.exitFullscreen)
                            await doc.exitFullscreen().catch(() => {})
                        else if (doc.webkitExitFullscreen)
                            await doc.webkitExitFullscreen()
                        else if (doc.mozCancelFullScreen)
                            await doc.mozCancelFullScreen()
                        else if (doc.msExitFullscreen) await doc.msExitFullscreen()
                    }
                } catch {}
            }
        } else if (playbackStatus.isPlaying && !wasPlaying) {
            if (!hasStarted) {
                setHasStarted(true)
            }
            setWasPlaying(true)
            mediaSingleton.setCurrentVideoRef(videoRef.current, instanceId)
            try {
                mediaSingleton.notifyPlaying(instanceId)
            } catch {}
            // Auto-ocultar overlay de controles 300ms después de iniciar reproducción (modo post)
            if (controlsMode === 'post') {
                if (controlsTimeoutRef.current) {
                    clearTimeout(controlsTimeoutRef.current)
                }
                setShowCustomControls(true)
                controlsOpacity.setValue(1)
                controlsTimeoutRef.current = setTimeout(() => {
                    Animated.timing(controlsOpacity, {
                        toValue: 0,
                        duration: 250,
                        useNativeDriver: true,
                    }).start(() => {
                        setShowCustomControls(false)
                        controlsOpacity.setValue(0)
                    })
                }, 300)
            }
        } else if (!playbackStatus.isPlaying && wasPlaying) {
            setWasPlaying(false)
        }

        if (
            lastPosition !== null &&
            playbackStatus.positionMillis > lastPosition + 1000 * playbackStatus.rate
        ) {
            // salto
        }

        if (playbackStatus.positionMillis > 2000) {
            let timeLeft =
                playbackStatus.durationMillis - playbackStatus.positionMillis

            const shouldTriggerNearEnd = isPodcast || path !== 'learning'

            if (
                timeLeft < 5000 &&
                !contentSeenTriggeredRef.current &&
                shouldTriggerNearEnd
            ) {
                contentSeenTriggeredRef.current = true
                contentSeen()
            }
        }
        setLastPosition(playbackStatus.positionMillis)

        // Si empezó a reproducir, aseguremos ocultar el overlay de play en web
        if (isWebPlatform() && playbackStatus.isPlaying && showPlayOverlay) {
            setShowPlayOverlay(false)
        }

        // Si está pausado (no playing), mostrar el overlay de play (web, modo post), salvo que haya terminado
        if (
            isWebPlatform() &&
            controlsMode === 'post' &&
            playbackStatus?.isLoaded &&
            !playbackStatus?.isPlaying &&
            !playbackStatus?.didJustFinish &&
            !endedRef.current
        ) {
            setShowPlayOverlay(true)
        }

        // Gate de reproducción para web: esperar 20% buffer si usuario ya pidió play
        if (WEB_DISABLE_AUTOPLAY && userRequestedPlay) {
            if (
                canStartAfterBuffer &&
                !playbackStatus.isPlaying &&
                !playbackStatus.isBuffering
            ) {
                try {
                    await videoRef.current?.playAsync()
                    setShowPlayOverlay(false)
                } catch {}
            } else if (!canStartAfterBuffer) {
                setWaitingForBufferGate(true)
            } else {
                setWaitingForBufferGate(false)
            }
        }
    }

    useEffect(() => {
        const configureAudio = async () => {
            try {
                await Audio.setAudioModeAsync({
                    interruptionModeIOS: 1,
                    playsInSilentModeIOS: true,
                    shouldDuckAndroid: false,
                    interruptionModeAndroid: 1,
                    playThroughEarpieceAndroid: false,
                })
                console.log(
                    '[VideoPlayerAV] Audio configured for mode:',
                    controlsMode,
                )
            } catch (error) {
                console.warn('[VideoPlayerAV] Error configuring audio:', error)
            }
        }

        configureAudio()
    }, [controlsMode]) // Agregar controlsMode como dependencia

    // Visibilidad: aplicar inmediatamente al ganar o perder visibilidad.
    // Pausar en cuanto sale de foco para evitar audio residual al seguir scrolleando.
    useEffect(() => {
        if (visibilityTimerRef.current) {
            clearTimeout(visibilityTimerRef.current)
            visibilityTimerRef.current = null
        }
        if (isVisible) setEffectiveVisible(true)
        else setEffectiveVisible(false)
        return () => {
            if (visibilityTimerRef.current) {
                clearTimeout(visibilityTimerRef.current)
                visibilityTimerRef.current = null
            }
        }
    }, [isVisible])

    // Pausa inmediata cuando pierde visibilidad (sin esperar a effectiveVisible)
    useEffect(() => {
        if (!isVisible && videoRef.current) {
            videoRef.current.pauseAsync().catch(() => {})
        }
    }, [isVisible])

    // Control centralizado de reproducción: evitamos shouldPlay para no provocar re-creaciones del elemento nativo
    useEffect(() => {
        const wantAuto = !!autoplay || !!externalShouldPlay
        const canPlay =
            effectiveVisible &&
            isReadyToPlay &&
            (!WEB_DISABLE_AUTOPLAY || userRequestedPlay)
        if (!videoRef.current) return

        // Si terminó y no hubo intención del usuario, no auto-reproducir
        if (endedRef.current) {
            videoRef.current.pauseAsync().catch(() => {})
            return
        }

        // Si no está visible, siempre pausar
        if (!effectiveVisible) {
            videoRef.current.pauseAsync().catch(() => {})
            return
        }

        // Si está visible y quiere autoplay O el usuario hizo clic en play
        if (wantAuto && canPlay) {
            videoRef.current.playAsync().catch(() => {})
            pendingAutoplayRef.current = false
        } else if (userRequestedPlay && canPlay) {
            // Usuario hizo clic en play, mantener reproduciendo mientras esté visible
            videoRef.current.playAsync().catch(() => {})
        }
        // Si no hay autoplay ni el usuario hizo clic, no hacer nada (no pausar automáticamente)
    }, [
        effectiveVisible,
        isReadyToPlay,
        autoplay,
        WEB_DISABLE_AUTOPLAY,
        userRequestedPlay,
        externalShouldPlay,
    ])

    useEffect(() => {
        if (!videoRef.current) return
        if (!effectiveVisible) {
            videoRef.current.pauseAsync().catch(() => {})
            mediaSingleton.pauseAllExcept(null)
            pendingAutoplayRef.current = false
            // Reset de estado de fin al salir de pantalla
            endedRef.current = false
            setShowReplayOverlay(false)
            return
        }
        mediaSingleton.pauseAllExcept(post_id)
    }, [effectiveVisible, post_id])

    // Manejo de AppState (Android/iOS): pausar al ir a background y reanudar de forma segura al volver
    useEffect(() => {
        const handleAppStateChange = async (nextState) => {
            try {
                const prev = appStateRef.current
                appStateRef.current = nextState
                const goingToBg = /inactive|background/.test(nextState)
                const returningActive =
                    /inactive|background/.test(prev) && nextState === 'active'

                if (goingToBg) {
                    // Guardar si estaba reproduciendo y pausar
                    try {
                        const s = await videoRef.current?.getStatusAsync()
                        wasPlayingBeforeBackgroundRef.current = !!s?.isPlaying
                    } catch {}
                    try {
                        await videoRef.current?.pauseAsync()
                    } catch {}
                    // Mostrar póster al volver para evitar flashes negros hasta que haya frames
                    try {
                        if (!posterOverlayVisibleRef.current) {
                            // reset overlay state
                            posterOverlayVisibleRef.current = true
                            posterOpacity.setValue(1)
                            setPosterOverlayVisible(true)
                        }
                    } catch {}
                } else if (returningActive) {
                    // Pequeño delay para permitir que RN re-hidrate vistas
                    setTimeout(async () => {
                        try {
                            const s = await videoRef.current?.getStatusAsync()
                            const canAutoPlay =
                                effectiveVisible &&
                                isReadyToPlay &&
                                (!WEB_DISABLE_AUTOPLAY || userRequestedPlay)
                            if (
                                wasPlayingBeforeBackgroundRef.current &&
                                !endedRef.current &&
                                canAutoPlay
                            ) {
                                await videoRef.current?.playAsync()
                                if (isWebPlatform()) setShowPlayOverlay(false)
                            }
                            // Si ya hay frames, el propio onPlaybackStatusUpdate ocultará el póster
                        } catch {}
                    }, 150)
                }
            } catch {}
        }
        const sub = AppState.addEventListener('change', handleAppStateChange)
        return () => sub?.remove()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveVisible, isReadyToPlay, WEB_DISABLE_AUTOPLAY, userRequestedPlay])

    // Cuando el player esté listo y haya un autoplay pendiente, arrancar
    useEffect(() => {
        if (!isReadyToPlay) return
        if (
            pendingAutoplayRef.current &&
            videoRef.current &&
            effectiveVisible &&
            !!autoplay
        ) {
            if (!WEB_DISABLE_AUTOPLAY || userRequestedPlay) {
                pendingAutoplayRef.current = false
                videoRef.current.playAsync().catch(() => {})
            }
        }
    }, [
        isReadyToPlay,
        effectiveVisible,
        autoplay,
        WEB_DISABLE_AUTOPLAY,
        userRequestedPlay,
    ])

    const fullscreenActiveRef = useRef(false)
    const onFullscreenUpdate = async ({ fullscreenUpdate }) => {
        // En web no tocar la orientación para evitar flashes/reflows innecesarios
        if (isWebPlatform()) return
        if (fullscreenUpdate === VideoFullscreenUpdate.PLAYER_DID_PRESENT) {
            fullscreenActiveRef.current = true
            try {
                typeof onFullscreenChange === 'function' && onFullscreenChange(true)
            } catch {}
            await ScreenOrientation.unlockAsync()
        } else if (fullscreenUpdate === VideoFullscreenUpdate.PLAYER_WILL_DISMISS) {
            fullscreenActiveRef.current = false
            try {
                typeof onFullscreenChange === 'function' && onFullscreenChange(false)
            } catch {}
            await ScreenOrientation.lockAsync(
                ScreenOrientation.OrientationLock.PORTRAIT,
            )
        }
    }
    useKeepAwake()

    // Ancho proporcionado por el padre (mobile / nativo). Para web mantenemos width 100% y dejamos que el layout de la columna lo determine.
    const widthPx =
        typeof containerWidth === 'number' && containerWidth > 0
            ? containerWidth
            : undefined

    // Altura natural: sin clamps ni normalizaciones, respetar ratio original del media.
    // Calcular siempre la altura exacta según el aspect ratio del video
    const computeTargetHeight = (w, r) => {
        if (!w || !r) return undefined
        const naturalHeight = Math.round(w / r)
        return Math.max(1, naturalHeight)
    }

    // Si el ratio quedó bloqueado por cache o naturalSize, priorizarlo siempre.
    const effectiveRatio =
        ratioLockedRef.current && ratio ? ratio : displayRatio || FALLBACK_RATIO
    const targetHeight = widthPx
        ? computeTargetHeight(widthPx, effectiveRatio)
        : undefined

    // Mantener altura estable basada en displayRatio; evitamos recalcular con naturalSize para esta sesión
    useEffect(() => {
        // noop: altura se calcula directamente desde displayRatio
    }, [displayRatio])

    // Timeout de rescate: si en X ms no llegó naturalSize, usamos fallback y mostramos el video
    useEffect(() => {
        if (!source) return
        if (hasNaturalSize) return
        const timeoutMs = isWebPlatform() ? 4000 : 1800
        const id = setTimeout(() => {
            if (!hasNaturalSize) {
                if (!isWebPlatform()) {
                    setDisplayRatio((prev) => prev || FALLBACK_RATIO)
                }
                if (isWebPlatform() && waitForNaturalSize) {
                    setShouldRenderVideo(true)
                }
                // Notificar listo aunque no haya metadata, respetando gate de prebuffer si corresponde
                maybeNotifyReadyFromStatus()
            }
        }, timeoutMs)
        return () => clearTimeout(id)
    }, [source, hasNaturalSize, maybeNotifyReadyFromStatus])

    // Extracción manual de metadata en Web
    useEffect(() => {
        if (!isWebPlatform()) return
        if (!source?.uri) return
        if (hasNaturalSize) return
        let cancelled = false
        let videoEl = document.createElement('video')
        try {
            videoEl.preload = 'metadata'
            videoEl.muted = true
            videoEl.src = source.uri
            const handler = () => {
                if (cancelled) return
                const w = videoEl.videoWidth
                const h = videoEl.videoHeight
                if (w && h && h > 0 && !ratioLockedRef.current) {
                    const nextRatio = w / h
                    if (nextRatio > MIN_VALID_RATIO && nextRatio < MAX_VALID_RATIO) {
                        ratioLockedRef.current = true
                        RATIO_CACHE.set(source.uri, nextRatio)
                        try {
                            SizeCache.setRatioForUrl?.(source.uri, nextRatio)
                        } catch {}
                        setRatio(nextRatio)
                        // Ajustar el layout si el ratio difiere significativamente del inicial
                        const prev = displayRatio || FALLBACK_RATIO
                        const relDiff =
                            Math.abs(nextRatio - prev) / Math.max(prev, 1e-6)
                        setHasNaturalSize(true)
                        if (waitForNaturalSize) setShouldRenderVideo(true)
                        if (relDiff > 0.12) {
                            try {
                                Animated.timing(containerOpacity, {
                                    toValue: 0.85,
                                    duration: 120,
                                    useNativeDriver: true,
                                }).start(() => {
                                    setDisplayRatio(nextRatio)
                                    Animated.timing(containerOpacity, {
                                        toValue: 1,
                                        duration: 180,
                                        useNativeDriver: true,
                                    }).start()
                                })
                            } catch {
                                setDisplayRatio(nextRatio)
                            }
                        } else {
                            setDisplayRatio(nextRatio)
                        }
                        // Listo a nivel de metadata; respetar gate de prebuffer si corresponde
                        maybeNotifyReadyFromStatus()
                    }
                }
                cleanup()
            }
            const errorHandler = () => {
                cleanup()
            }
            const cleanup = () => {
                if (videoEl) {
                    videoEl.removeEventListener('loadedmetadata', handler)
                    videoEl.removeEventListener('error', errorHandler)
                    videoEl.src = ''
                    videoEl.load?.()
                    videoEl = null
                }
            }
            videoEl.addEventListener('loadedmetadata', handler)
            videoEl.addEventListener('error', errorHandler)
        } catch (e) {
            // Silencio
        }
        return () => {
            cancelled = true
            if (videoEl) {
                videoEl.src = ''
                videoEl.load?.()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source?.uri, isWebPlatform(), hasNaturalSize])

    // Overlay de póster: visible hasta que el primer frame esté listo
    const posterOverlayVisibleRef = useRef(true)
    const [posterOverlayVisible, setPosterOverlayVisible] = useState(true)
    const posterOpacity = useRef(new Animated.Value(1)).current
    const hidePosterOverlay = useCallback(() => {
        if (!posterOverlayVisibleRef.current) return
        posterOverlayVisibleRef.current = false
        // Quitar fade: ocultar inmediatamente
        try {
            posterOpacity.setValue(0)
        } catch {}
        // Solo actualizar estado si es necesario para el render
        setPosterOverlayVisible(false)
    }, [posterOpacity])

    // Controles en hover para modo minimal (web)
    const [hovering, setHovering] = useState(false)
    // Fade suave del contenedor cuando corregimos el ratio real en web
    const containerOpacity = useRef(new Animated.Value(1)).current
    const HoverControlsMinimal = useCallback(() => {
        if (!isWebPlatform() || controlsMode !== 'minimal') return null
        if (!hovering) return null
        return (
            <View
                style={{ ...StyleSheet.absoluteFillObject }}
                pointerEvents='box-none'
            >
                {/* Botón sonido */}
                <View
                    style={{ position: 'absolute', top: 16, left: 16, zIndex: 12 }}
                >
                    <TouchableOpacity
                        style={{
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            padding: 10,
                            borderRadius: 22,
                        }}
                        onPress={handleMuteToggle}
                        activeOpacity={0.85}
                    >
                        <Feather
                            name={isMutedMinimal ? 'volume-x' : 'volume-2'}
                            size={18}
                            color='#fff'
                        />
                    </TouchableOpacity>
                </View>
                {/* Botón Play/Pause centrado */}
                <View
                    style={{
                        ...StyleSheet.absoluteFillObject,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    pointerEvents='box-none'
                >
                    <TouchableOpacity
                        onPress={handlePlayPause}
                        activeOpacity={0.9}
                        style={{
                            backgroundColor: 'rgba(0,0,0,0.45)',
                            padding: 16,
                            borderRadius: 48,
                        }}
                    >
                        {status?.isPlaying ? (
                            <Feather name='pause' size={28} color='white' />
                        ) : (
                            <Feather name='play' size={28} color='white' />
                        )}
                    </TouchableOpacity>
                </View>
            </View>
        )
    }, [hovering, controlsMode, status?.isPlaying, isMutedMinimal])

    // No renderizar Video hasta que source esté definido
    if (!source) {
        return (
            <View
                style={[
                    isPodcast ? styles.containerPodcast : styles.container,
                    {
                        backgroundColor: '#000',
                        borderRadius: 0,
                        aspectRatio: 16 / 9,
                    },
                ]}
            />
        )
    }

    // Si se requiere esperar naturalSize en web, no renderizar el Video hasta tenerlo (o timeout)
    if (isWebPlatform() && waitForNaturalSize && !shouldRenderVideo) {
        return (
            <View
                style={[
                    isPodcast ? styles.containerPodcast : styles.container,
                    {
                        backgroundColor: '#000',
                        borderRadius: 0,
                        aspectRatio: displayRatio || FALLBACK_RATIO,
                    },
                ]}
            />
        )
    }

    const handlePressPlay = async () => {
        if (!videoRef.current) return
        try {
            typeof onUserPlayIntent === 'function' && onUserPlayIntent()
        } catch {}
        setUserRequestedPlay(true)
        if (WEB_DISABLE_AUTOPLAY) {
            if (canStartAfterBuffer || bufferPercent === 0) {
                try {
                    await videoRef.current.playAsync()
                    setShowPlayOverlay(false)
                } catch {}
            } else {
                setWaitingForBufferGate(true)
            }
        } else {
            try {
                await videoRef.current.playAsync()
                setShowPlayOverlay(false)
            } catch {}
        }
    }

    // Funciones para controles personalizados del modo 'post'
    const showControlsTemporarily = () => {
        if (controlsMode !== 'post') return

        setShowCustomControls(true)
        controlsOpacity.setValue(1)

        // Limpiar timeout anterior
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current)
        }

        // Ocultar controles después de 300ms con fade
        controlsTimeoutRef.current = setTimeout(() => {
            Animated.timing(controlsOpacity, {
                toValue: 0,
                duration: 250,
                useNativeDriver: true,
            }).start(() => {
                setShowCustomControls(false)
                // Mantener en 0 para que no reaparezcan inmediatamente
                controlsOpacity.setValue(0)
            })
        }, 300)
    }

    const handleVideoPress = () => {
        if (controlsMode !== 'post') return
        if (Platform.OS !== 'web' && onVideoPress) {
            onVideoPress()
        }
    }

    const handleLongPress = () => {
        if (controlsMode === 'post' && Platform.OS !== 'web') {
            // Mostrar selector de reacciones
            if (onLongPress) {
                if (DEBUG_GESTURES)
                    console.log(
                        '[VideoPlayerAV][DEBUG] Long press base -> abrir reacciones',
                    )
                onLongPress()
            }
        }
    }

    // Capa base: single tap -> onVideoPress (reels), double tap -> reacción, long press -> picker
    const handleBasePress = () => {
        if (controlsMode !== 'post') return
        const now = Date.now()
        const DOUBLE_TAP_DELAY = 260
        if (now - (lastTapRef.current || 0) < DOUBLE_TAP_DELAY) {
            // Double tap detectado
            lastTapRef.current = 0
            if (singleTapTimeoutRef.current) {
                clearTimeout(singleTapTimeoutRef.current)
                singleTapTimeoutRef.current = null
            }
            try {
                if (DEBUG_GESTURES)
                    console.log(
                        '[VideoPlayerAV][DEBUG] Double tap base -> reacción like/dislike',
                    )
                typeof onDoubleTap === 'function' && onDoubleTap()
            } catch {}
        } else {
            lastTapRef.current = now
            // Diferir el single tap para distinguir de double tap
            if (singleTapTimeoutRef.current) {
                clearTimeout(singleTapTimeoutRef.current)
                singleTapTimeoutRef.current = null
            }
            singleTapTimeoutRef.current = setTimeout(() => {
                lastTapRef.current = 0
                try {
                    if (DEBUG_GESTURES)
                        console.log(
                            '[VideoPlayerAV][DEBUG] Single tap base -> abrir Reels',
                        )
                    handleVideoPress()
                } catch {}
            }, DOUBLE_TAP_DELAY + 10)
        }
    }

    const handleMuteToggle = async () => {
        try {
            if (videoRef.current) {
                if (controlsMode === 'post') {
                    const newMutedState = !isMuted
                    await videoRef.current.setVolumeAsync(newMutedState ? 0 : 1)
                    setIsMuted(newMutedState)
                } else {
                    const newMutedState = !isMutedMinimal
                    await videoRef.current.setVolumeAsync(newMutedState ? 0 : 1)
                    setIsMutedMinimal(newMutedState)
                }
                showControlsTemporarily() // Extender tiempo de controles
            }
        } catch (error) {
            console.warn('Error toggling mute:', error)
        }
    }

    const handlePlayPause = async () => {
        try {
            if (videoRef.current) {
                if (status?.isPlaying) {
                    await videoRef.current.pauseAsync()
                    // Al pausar manualmente, mostrar overlay de play persistente en web
                    if (isWebPlatform() && controlsMode === 'post')
                        setShowPlayOverlay(true)
                } else {
                    // Gesto del usuario -> habilita autoplay web
                    setUserRequestedPlay(true)
                    // En modo minimal (learnings), al iniciar manualmente, desmutear para que haya sonido
                    if (controlsMode === 'minimal') {
                        try {
                            await videoRef.current.setVolumeAsync(1)
                            // Intentar forzar atributo muted=false en la plataforma
                            if (
                                typeof videoRef.current.setIsMutedAsync ===
                                'function'
                            ) {
                                await videoRef.current.setIsMutedAsync(false)
                            }
                        } catch {}
                        setIsMutedMinimal(false)
                    }
                    await videoRef.current.playAsync()
                    if (isWebPlatform()) setShowPlayOverlay(false)
                }
                showControlsTemporarily()
            }
        } catch (error) {
            console.warn('Error toggling play/pause:', error)
        }
    }

    const handleReplay = async () => {
        try {
            if (videoRef.current) {
                await videoRef.current.setPositionAsync(0)
                endedRef.current = false
                setShowReplayOverlay(false)
                await videoRef.current.playAsync()
                showControlsTemporarily()
            }
        } catch (error) {
            console.warn('Error on replay:', error)
        }
    }

    const handleFullscreen = async () => {
        try {
            if (videoRef.current) {
                await videoRef.current.presentFullscreenPlayer()
                showControlsTemporarily()
            }
        } catch (error) {
            console.warn('Error entering fullscreen:', error)
        }
    }

    // Render del video (web/nativo)
    // Función regular (no useCallback) para no alterar el orden de hooks
    const applyNaturalSize = (size) => {
        // Proteger contra múltiples ejecuciones
        if (naturalSizeAppliedRef.current) return

        try {
            const w = size?.width
            const h = size?.height
            if (w && h && h > 0) {
                let nextRatio = w / h
                const orientation = String(size?.orientation || '').toLowerCase()
                if (orientation === 'portrait' && w > h) {
                    nextRatio = h / w
                }
                if (nextRatio > MIN_VALID_RATIO && nextRatio < MAX_VALID_RATIO) {
                    // Marcar como aplicado ANTES de cualquier operación asíncrona
                    naturalSizeAppliedRef.current = true
                    ratioLockedRef.current = true

                    // Persistir para futuras monturas
                    if (source?.uri) {
                        RATIO_CACHE.set(source.uri, nextRatio)
                        try {
                            SizeCache.setRatioForUrl?.(source.uri, nextRatio)
                        } catch {}
                    }
                    setIsReadyToPlay(true)
                    // No disparemos listo inmediatamente: respetar gate de prebuffer/primer frame
                    maybeNotifyReadyFromStatus()
                    try {
                        typeof onNaturalSize === 'function' &&
                            onNaturalSize({ width: w, height: h, ratio: nextRatio })
                    } catch {}
                    // Actualización de layout: preferimos estabilidad, pero si el ratio real difiere mucho del estimado,
                    // en web corregimos la relación de aspecto del contenedor para evitar que se vea "chico" o mal ajustado.
                    // Umbral: 12% de diferencia relativa.
                    const prev = displayRatio || FALLBACK_RATIO
                    const relDiff = Math.abs(nextRatio - prev) / Math.max(prev, 1e-6)

                    setRatio(nextRatio)
                    setHasNaturalSize(true)

                    // Actualizar displayRatio para que el contenedor se ajuste al ratio real
                    // En web: solo si hay diferencia significativa
                    // En mobile: siempre actualizar para que el contenedor respete el aspect ratio exacto
                    if (isWebPlatform()) {
                        if (
                            relDiff > 0.12 &&
                            displayRatio === (initialDisplayRatio || FALLBACK_RATIO)
                        ) {
                            setDisplayRatio(nextRatio)
                        }
                    } else {
                        // En mobile siempre actualizar para respetar el aspect ratio del video
                        setDisplayRatio(nextRatio)
                    }
                }
            }
        } catch {}
    }

    const fullScreenRatio = screenWidth / screenHeight

    const finalWidth = screenWidth

    let finalHeight = finalWidth / fullScreenRatio
    if (finalHeight > screenHeight) {
        finalHeight = screenHeight
    }

    const renderVideoElement = () => {
        const videoBaseStyle = [
            isPodcast ? styles.videoPodcast : styles.video,
            { flex: 1, width: '100%', height: '100%', backgroundColor: '#000' },
        ]

        if (isWebPlatform()) {
            const ratioForLayout = displayRatio || FALLBACK_RATIO
            return (
                <Animated.View
                    style={[
                        {
                            width: '100%',
                            aspectRatio: ratioForLayout,
                            borderRadius: 0,
                            overflow: 'hidden',
                            backgroundColor: '#000',
                            willChange: 'transform',
                        },
                        isFullScreen && {
                            width: finalWidth,
                            height: finalHeight,
                            aspectRatio: undefined,
                        },
                    ]}
                    onMouseEnter={() => setHovering(true)}
                    onMouseLeave={() => setHovering(false)}
                >
                    <Video
                        ref={videoRef}
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            backgroundColor: '#000',
                            objectFit: 'contain', // 👈 clave para web
                        }}
                        videoStyle={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            backgroundColor: '#000',
                            objectFit: 'contain', // 👈 clave para web
                        }}
                        source={source}
                        progressUpdateIntervalMillis={400}
                        useNativeControls={controlsMode === 'native'}
                        rate={1.0}
                        isMuted={
                            controlsMode === 'post' ? !!isMuted : !!isMutedMinimal
                        }
                        volume={
                            controlsMode === 'post'
                                ? isMuted
                                    ? 0
                                    : 1
                                : isMutedMinimal
                                  ? 0
                                  : 1
                        }
                        isLooping={false}
                        resizeMode={ResizeMode.CONTAIN}
                        onPlaybackStatusUpdate={onPlaybackStatusUpdate}
                        defaultControlsVisible={controlsMode === 'native'}
                        onFullscreenUpdate={(status) => onFullscreenUpdate(status)}
                        // En Web evitamos el póster interno de expo-av para no duplicar capas
                        usePoster={false}
                        posterSource={posterSource}
                        onError={(e) => {
                            try {
                                typeof onReady === 'function' && onReady()
                            } catch {}
                        }}
                        onLoad={(e) => {
                            setIsReadyToPlay(true)
                            applyNaturalSize(e?.naturalSize)
                        }}
                        onReadyForDisplay={(e) => {
                            setIsReadyToPlay(true)
                            applyNaturalSize(e?.naturalSize)
                            // Pequeño delay para asegurar que el frame esté realmente visible antes de ocultar póster
                            setTimeout(() => {
                                // Solo ocultar si hay posición > 0 o está reproduciendo
                                if (posterOverlayVisibleRef.current) {
                                    const shouldHide =
                                        status?.isPlaying ||
                                        (status?.positionMillis || 0) > 0
                                    if (shouldHide) {
                                        hidePosterOverlay()
                                    }
                                }
                            }, 80)
                        }}
                    />
                    {/* Póster superpuesto hasta que el primer frame esté listo */}
                    {posterOverlayVisible && (
                        <Animated.View
                            pointerEvents='none'
                            style={{
                                ...StyleSheet.absoluteFillObject,
                                opacity: posterOpacity,
                                backgroundColor: '#000',
                            }}
                        >
                            {posterSource?.uri ? (
                                <Image
                                    source={posterSource}
                                    resizeMode='contain'
                                    style={{ width: '100%', height: '100%' }}
                                />
                            ) : null}
                        </Animated.View>
                    )}
                    {showPlayOverlay && (
                        <View
                            style={{
                                ...StyleSheet.absoluteFillObject,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            pointerEvents='box-none'
                        >
                            <TouchableOpacity
                                style={{
                                    backgroundColor: 'rgba(0,0,0,0.55)',
                                    padding: 18,
                                    borderRadius: 48,
                                }}
                                dataSet={{
                                    role: 'player-control',
                                    control: 'play-overlay',
                                }}
                                onPress={handlePressPlay}
                                activeOpacity={0.85}
                            >
                                <Feather name='play' size={36} color='white' />
                            </TouchableOpacity>
                            {waitingForBufferGate && (
                                <Text
                                    style={{
                                        color: 'white',
                                        marginTop: 12,
                                        fontSize: 12,
                                        textShadowColor: 'rgba(0,0,0,0.5)',
                                        textShadowRadius: 2,
                                    }}
                                >
                                    Preparando… {Math.round(bufferPercent * 100)}%
                                </Text>
                            )}
                        </View>
                    )}
                    {/* Modo minimal (learnings) -> mostrar controles sólo con hover en web */}
                    {controlsMode === 'minimal' && isWebPlatform() && (
                        <HoverControlsMinimal />
                    )}
                    {controlsMode === 'post' && (
                        <View
                            style={{ ...StyleSheet.absoluteFillObject }}
                            pointerEvents='box-none'
                        >
                            {/* Capa base: en web -> play/pause; en mobile -> taps/reacciones */}
                            <TouchableOpacity
                                style={{ ...StyleSheet.absoluteFillObject }}
                                activeOpacity={1}
                                onPress={
                                    Platform.OS === 'web'
                                        ? handlePlayPause
                                        : handleBasePress
                                }
                                onLongPress={
                                    Platform.OS !== 'web'
                                        ? handleLongPress
                                        : undefined
                                }
                                delayLongPress={650}
                            />
                            {/* Botón de sonido siempre visible con 20% de opacidad (hover 100% en web) */}
                            <Animated.View
                                style={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    zIndex: 10,
                                    opacity: isWebPlatform() && muteHover ? 1 : 0.2,
                                    transform: [{ scale: muteScale }],
                                }}
                                onMouseEnter={() =>
                                    isWebPlatform() && setMuteHover(true)
                                }
                                onMouseLeave={() =>
                                    isWebPlatform() && setMuteHover(false)
                                }
                            >
                                <TouchableOpacity
                                    style={{
                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                        padding: 12,
                                        borderRadius: 25,
                                    }}
                                    dataSet={{
                                        role: 'player-control',
                                        control: 'sound',
                                    }}
                                    onPress={handleMuteToggle}
                                    onPressIn={onMutePressIn}
                                    onPressOut={onMutePressOut}
                                    activeOpacity={0.8}
                                >
                                    <Feather
                                        name={isMuted ? 'volume-x' : 'volume-2'}
                                        size={20}
                                        color='white'
                                    />
                                </TouchableOpacity>
                            </Animated.View>
                            {/* Overlay de "Volver a ver" al finalizar (solo si no es gestionado externamente) */}
                            {!manageReplayOverlayExternally && showReplayOverlay && (
                                <View
                                    style={{
                                        ...StyleSheet.absoluteFillObject,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    pointerEvents='box-none'
                                >
                                    <TouchableOpacity
                                        onPress={handleReplay}
                                        activeOpacity={0.9}
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            paddingVertical: 12,
                                            paddingHorizontal: 16,
                                            borderRadius: 28,
                                        }}
                                    >
                                        <Feather
                                            name='rotate-ccw'
                                            size={20}
                                            color='#fff'
                                        />
                                        <Text
                                            style={{
                                                color: '#fff',
                                                marginLeft: 8,
                                                fontWeight: '600',
                                            }}
                                        >
                                            Volver a ver
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    )}
                    {isLearning && hovering && (
                        <View
                            style={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                paddingHorizontal: 12,
                                paddingBottom: 10,
                                paddingTop: 8,
                                backgroundColor: 'rgba(0,0,0,0.28)',
                            }}
                        >
                            <View
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                }}
                            >
                                <TouchableOpacity
                                    onPress={async () => {
                                        handlePlayPause()
                                    }}
                                    style={{ padding: 6, marginRight: 6 }}
                                    hitSlop={{
                                        top: 6,
                                        left: 6,
                                        right: 6,
                                        bottom: 6,
                                    }}
                                >
                                    {status?.isPlaying ? (
                                        <Feather
                                            name='pause'
                                            size={16}
                                            color='#fff'
                                        />
                                    ) : (
                                        <Feather
                                            name='play'
                                            size={16}
                                            color='#fff'
                                        />
                                    )}
                                </TouchableOpacity>
                                {/* progress */}
                                <View
                                    style={{
                                        flex: 1,
                                        height: 22,
                                        justifyContent: 'center',
                                    }}
                                >
                                    <View
                                        ref={inlineTrackRef}
                                        style={{
                                            height: 4,
                                            backgroundColor:
                                                'rgba(255,255,255,0.25)',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <View
                                            style={{
                                                height: 4,
                                                width: `${status?.durationMillis > 0 ? (status?.positionMillis / status?.durationMillis) * 100 : 0}%`,
                                                backgroundColor: '#fff',
                                                borderRadius: 2,
                                            }}
                                        />
                                    </View>
                                    <View
                                        onLayout={(e) => {
                                            inlineTrackWidthRef.current =
                                                e?.nativeEvent?.layout?.width || 0
                                        }}
                                        style={{ ...StyleSheet.absoluteFillObject }}
                                    >
                                        <TouchableOpacity
                                            onPress={async (e) => {
                                                try {
                                                    const rect =
                                                        inlineTrackRef.current?.getBoundingClientRect?.()
                                                    const pageX =
                                                        e?.nativeEvent?.pageX ??
                                                        e?.nativeEvent?.clientX
                                                    const w =
                                                        rect?.width ||
                                                        inlineTrackWidthRef.current ||
                                                        0
                                                    const x =
                                                        typeof pageX === 'number' &&
                                                        rect
                                                            ? pageX - rect.left
                                                            : e?.nativeEvent
                                                                  ?.locationX || 0
                                                    const dur =
                                                        status?.durationMillis || 0
                                                    if (w > 0 && dur > 0) {
                                                        const pct = Math.max(
                                                            0,
                                                            Math.min(1, x / w),
                                                        )
                                                        const newPos = Math.round(
                                                            dur * pct,
                                                        )
                                                        const wasPlaying =
                                                            !!status?.isPlaying
                                                        await videoRef.current?.setPositionAsync(
                                                            newPos,
                                                        )
                                                        if (wasPlaying)
                                                            await videoRef.current?.playAsync()
                                                    }
                                                } catch {}
                                            }}
                                            activeOpacity={1}
                                            style={{ width: '100%', height: '100%' }}
                                            onStartShouldSetResponder={() =>
                                                Platform.OS === 'web'
                                            }
                                            onResponderGrant={async (ev) => {
                                                if (Platform.OS !== 'web') return
                                                try {
                                                    const rect =
                                                        inlineTrackRef.current.getBoundingClientRect?.()
                                                    const pageX =
                                                        ev?.nativeEvent?.pageX ??
                                                        ev?.nativeEvent?.clientX
                                                    const w =
                                                        rect?.width ||
                                                        inlineTrackWidthRef.current ||
                                                        0
                                                    const dur =
                                                        status?.durationMillis || 0
                                                    if (
                                                        !w ||
                                                        !dur ||
                                                        typeof pageX !== 'number'
                                                    )
                                                        return
                                                    const pct = Math.max(
                                                        0,
                                                        Math.min(
                                                            1,
                                                            (pageX - rect.left) / w,
                                                        ),
                                                    )
                                                    const newPos = Math.round(
                                                        dur * pct,
                                                    )
                                                    const st = inlinePlayback[index]
                                                    const wasPlaying =
                                                        !!st?.isPlaying
                                                    if (wasPlaying)
                                                        await videoRef.current?.pauseAsync()
                                                    await videoRef.current?.setPositionAsync(
                                                        newPos,
                                                    )
                                                    if (wasPlaying)
                                                        await videoRef.current?.playAsync()
                                                } catch {}
                                            }}
                                        />
                                    </View>
                                </View>
                                <Text
                                    style={{
                                        color: '#fff',
                                        fontSize: 10,
                                        marginLeft: 6,
                                    }}
                                >
                                    {(() => {
                                        const pos = Math.max(
                                            0,
                                            status?.positionMillis || 0,
                                        )
                                        const dur = Math.max(
                                            0,
                                            status?.durationMillis || 0,
                                        )
                                        const toTime = (ms) => {
                                            const s = Math.floor((ms || 0) / 1000)
                                            const m = Math.floor(s / 60)
                                            const ss = s % 60
                                            return `${m}:${ss < 10 ? '0' : ''}${ss}`
                                        }
                                        return `${toTime(pos)} / ${toTime(dur)}`
                                    })()}
                                </Text>
                            </View>
                        </View>
                    )}
                </Animated.View>
            )
        }
        // Nativo: si tenemos width provisto (layout padre), usamos altura fija derivada del ratio (cacheado si existe).
        if (widthPx) {
            // Usar el ratio real detectado si está disponible, sino usar displayRatio
            // Esto asegura que el contenedor se ajuste al aspect ratio real del video
            const layoutRatio = ratio || displayRatio || effectiveRatio

            // Usar COVER para llenar todo el espacio disponible sin márgenes
            // El contenedor se ajusta dinámicamente a la altura exacta según el aspect ratio
            const resizeMode = ResizeMode.COVER

            // DEBUG: Log para verificar shouldPlay en mobile
            const shouldPlayValue =
                !WEB_DISABLE_AUTOPLAY && autoplay && effectiveVisible
            let debugFalse = false

            return (
                <View
                    style={{
                        width: widthPx,
                        height: computeTargetHeight(widthPx, layoutRatio),
                        backgroundColor: '#000',
                        borderRadius: 0,
                        overflow: 'hidden',
                    }}
                >
                    <Video
                        key={`video-WidthPX-${retryKey}`}
                        ref={videoRef}
                        style={videoBaseStyle}
                        source={source}
                        progressUpdateIntervalMillis={400}
                        useNativeControls={controlsMode === 'native'}
                        rate={1.0}
                        isMuted={
                            controlsMode === 'post' ? !!isMuted : !!isMutedMinimal
                        }
                        volume={
                            controlsMode === 'post'
                                ? isMuted
                                    ? 0
                                    : 1
                                : isMutedMinimal
                                  ? 0
                                  : 1
                        }
                        isLooping={false}
                        resizeMode={resizeMode}
                        onPlaybackStatusUpdate={onPlaybackStatusUpdate}
                        defaultControlsVisible={controlsMode === 'native'}
                        onFullscreenUpdate={(status) => onFullscreenUpdate(status)}
                        usePoster={true}
                        posterSource={posterSource}
                        onError={(e) => {
                            try {
                                setVideoError(true)
                                typeof onReady === 'function' && onReady()
                            } catch {}
                        }}
                        onLoad={(e) => {
                            setIsReadyToPlay(true)
                            setVideoLoaded(true)
                            applyNaturalSize(e?.naturalSize)
                        }}
                        onReadyForDisplay={(e) => {
                            setIsReadyToPlay(true)
                            applyNaturalSize(e?.naturalSize)
                            // Delay para asegurar frame visible
                            setTimeout(() => {
                                if (posterOverlayVisibleRef.current) {
                                    const shouldHide =
                                        status?.isPlaying ||
                                        (status?.positionMillis || 0) > 0
                                    if (shouldHide) hidePosterOverlay()
                                }
                            }, 80)
                        }}
                    />
                    {/* Póster superpuesto hasta que el primer frame esté listo (nativo con width fijo) */}
                    {posterOverlayVisible && (
                        <Animated.View
                            pointerEvents='none'
                            style={{
                                ...StyleSheet.absoluteFillObject,
                                opacity: posterOpacity,
                                backgroundColor: '#000',
                            }}
                        >
                            {posterSource?.uri ? (
                                <Image
                                    source={posterSource}
                                    resizeMode='contain'
                                    style={{ width: '100%', height: '100%' }}
                                />
                            ) : null}
                        </Animated.View>
                    )}
                    {controlsMode === 'minimal' && (
                        <TouchableOpacity
                            style={{
                                ...StyleSheet.absoluteFillObject,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            activeOpacity={0.9}
                            dataSet={{
                                role: 'player-control',
                                control: 'playpause',
                            }}
                            onPress={async () => {
                                try {
                                    const s =
                                        await videoRef.current?.getStatusAsync()
                                    if (s?.isPlaying) {
                                        await videoRef.current?.pauseAsync()
                                    } else {
                                        await videoRef.current?.playAsync()
                                    }
                                } catch {}
                            }}
                        >
                            {!wasPlaying && (
                                <View
                                    style={{
                                        backgroundColor: 'rgba(0,0,0,0.35)',
                                        padding: 18,
                                        borderRadius: 48,
                                    }}
                                >
                                    <Feather name='play' size={36} color='white' />
                                </View>
                            )}
                        </TouchableOpacity>
                    )}
                    {controlsMode === 'minimal' && isWebPlatform() && (
                        <HoverControlsMinimal />
                    )}
                    {controlsMode === 'post' && (
                        <View
                            style={{ ...StyleSheet.absoluteFillObject }}
                            pointerEvents='box-none'
                        >
                            {/* Layer base: en mobile gestiona reels/reacciones; en web, single tap -> play/pause */}
                            <TouchableOpacity
                                style={{
                                    ...StyleSheet.absoluteFillObject,
                                    zIndex: 10000,
                                    ...(Platform.OS !== 'web' && DEBUG_GESTURES
                                        ? {
                                              backgroundColor: 'rgba(255,0,0,0.12)',
                                              borderWidth: 1,
                                              borderColor: 'rgba(255,0,0,0.6)',
                                          }
                                        : null),
                                }}
                                activeOpacity={1}
                                onPress={
                                    Platform.OS === 'web'
                                        ? handlePlayPause
                                        : handleBasePress
                                }
                                onLongPress={() => {
                                    if (typeof onLongPress === 'function')
                                        onLongPress()
                                }}
                                delayLongPress={650}
                                onPressOut={() => {
                                    // no-op
                                }}
                            />
                            {/* Botón de Play/Pause centrado (por encima) */}
                            {(() => {
                                const pausedOverlay =
                                    Platform.OS !== 'web' &&
                                    controlsMode === 'post' &&
                                    status?.isLoaded &&
                                    !status?.isPlaying &&
                                    !endedRef.current
                                const showCenter =
                                    showCustomControls || pausedOverlay
                                if (!showCenter) return null
                                const centerOpacity = pausedOverlay
                                    ? 1
                                    : controlsOpacity
                                return (
                                    <Animated.View
                                        pointerEvents='box-none'
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10001,
                                            opacity: centerOpacity,
                                        }}
                                    >
                                        <View
                                            pointerEvents='auto'
                                            dataSet={{
                                                role: 'player-control',
                                                control: 'playpause',
                                            }}
                                        >
                                            <TouchableOpacity
                                                onPress={handlePlayPause}
                                                activeOpacity={0.9}
                                                style={{
                                                    backgroundColor:
                                                        'rgba(0,0,0,0.45)',
                                                    padding: 18,
                                                    borderRadius: 48,
                                                }}
                                            >
                                                {status?.isPlaying ? (
                                                    <Feather
                                                        name='pause'
                                                        size={36}
                                                        color='white'
                                                    />
                                                ) : (
                                                    <Feather
                                                        name='play'
                                                        size={36}
                                                        color='white'
                                                    />
                                                )}
                                            </TouchableOpacity>
                                        </View>
                                    </Animated.View>
                                )
                            })()}
                            {/* Botón de sonido (esquina superior derecha) con opacidad fija 20% y micro-escala al presionar */}
                            <Animated.View
                                pointerEvents='box-none'
                                style={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    zIndex: 10002,
                                    opacity: 0.7,
                                    transform: [{ scale: muteScale }],
                                }}
                            >
                                <TouchableOpacity
                                    style={{
                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                        padding: 12,
                                        borderRadius: 25,
                                        ...(DEBUG_GESTURES
                                            ? {
                                                  borderWidth: 1,
                                                  borderColor: 'rgba(0,255,0,0.8)',
                                              }
                                            : null),
                                    }}
                                    dataSet={{
                                        role: 'player-control',
                                        control: 'sound',
                                    }}
                                    onPress={handleMuteToggle}
                                    onPressIn={onMutePressIn}
                                    onPressOut={onMutePressOut}
                                    activeOpacity={0.8}
                                >
                                    <Feather
                                        name={isMuted ? 'volume-x' : 'volume-2'}
                                        size={20}
                                        color='white'
                                    />
                                </TouchableOpacity>
                            </Animated.View>
                            {/* Overlay de "Volver a ver" al finalizar (solo si no es gestionado externamente) */}
                            {!manageReplayOverlayExternally && showReplayOverlay && (
                                <View
                                    style={{
                                        ...StyleSheet.absoluteFillObject,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    pointerEvents='box-none'
                                >
                                    <TouchableOpacity
                                        onPress={handleReplay}
                                        activeOpacity={0.9}
                                        dataSet={{
                                            role: 'player-control',
                                            control: 'replay',
                                        }}
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            paddingVertical: 12,
                                            paddingHorizontal: 16,
                                            borderRadius: 28,
                                        }}
                                    >
                                        <Feather
                                            name='rotate-ccw'
                                            size={20}
                                            color='#fff'
                                        />
                                        <Text
                                            style={{
                                                color: '#fff',
                                                marginLeft: 8,
                                                fontWeight: '600',
                                            }}
                                        >
                                            Volver a ver
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    )}
                </View>
            )
        }
        // Sin width específico: usamos aspectRatio directamente
        const finalResizeMode =
            effectiveRatio < 0.75 ? ResizeMode.COVER : ResizeMode.CONTAIN
        return (
            <View style={{ width: '100%', aspectRatio: effectiveRatio }}>
                <Video
                    key={`video-aspect-${retryKey}`}
                    ref={videoRef}
                    style={videoBaseStyle}
                    source={source}
                    progressUpdateIntervalMillis={400}
                    useNativeControls={controlsMode === 'native'}
                    rate={1.0}
                    isMuted={controlsMode === 'post' ? !!isMuted : !!isMutedMinimal}
                    volume={
                        controlsMode === 'post'
                            ? isMuted
                                ? 0
                                : 1
                            : isMutedMinimal
                              ? 0
                              : 1
                    }
                    isLooping={false}
                    resizeMode={finalResizeMode}
                    onPlaybackStatusUpdate={onPlaybackStatusUpdate}
                    defaultControlsVisible={controlsMode === 'native'}
                    onFullscreenUpdate={(status) => onFullscreenUpdate(status)}
                    usePoster={true}
                    posterSource={posterSource}
                    onError={(e) => {
                        try {
                            setVideoError(true)
                            typeof onReady === 'function' && onReady()
                        } catch {}
                    }}
                    onLoad={(e) => {
                        setIsReadyToPlay(true)
                        setVideoLoaded(true)
                        applyNaturalSize(e?.naturalSize)
                    }}
                    onReadyForDisplay={(e) => {
                        setIsReadyToPlay(true)
                        applyNaturalSize(e?.naturalSize)
                        // Delay para asegurar frame visible
                        setTimeout(() => {
                            if (posterOverlayVisibleRef.current) {
                                const shouldHide =
                                    status?.isPlaying ||
                                    (status?.positionMillis || 0) > 0
                                if (shouldHide) hidePosterOverlay()
                            }
                        }, 80)
                    }}
                />
                {/* Póster superpuesto hasta que el primer frame esté listo (sin width explícito) */}
                {posterOverlayVisible && (
                    <Animated.View
                        pointerEvents='none'
                        style={{
                            ...StyleSheet.absoluteFillObject,
                            opacity: posterOpacity,
                            backgroundColor: '#000',
                        }}
                    >
                        {posterSource?.uri ? (
                            <Image
                                source={posterSource}
                                resizeMode='contain'
                                style={{ width: '100%', height: '100%' }}
                            />
                        ) : null}
                    </Animated.View>
                )}
                {controlsMode === 'minimal' && (
                    <TouchableOpacity
                        style={{
                            ...StyleSheet.absoluteFillObject,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        activeOpacity={0.9}
                        onPress={async () => {
                            try {
                                const s = await videoRef.current?.getStatusAsync()
                                if (s?.isPlaying) {
                                    await videoRef.current?.pauseAsync()
                                } else {
                                    await videoRef.current?.playAsync()
                                }
                            } catch {}
                        }}
                    >
                        {!wasPlaying && (
                            <View
                                style={{
                                    backgroundColor: 'rgba(0,0,0,0.35)',
                                    padding: 18,
                                    borderRadius: 48,
                                }}
                            >
                                <Feather name='play' size={36} color='white' />
                            </View>
                        )}
                    </TouchableOpacity>
                )}
                {controlsMode === 'minimal' && isWebPlatform() && (
                    <HoverControlsMinimal />
                )}
                {controlsMode === 'post' && (
                    <View
                        style={{ ...StyleSheet.absoluteFillObject }}
                        pointerEvents='box-none'
                    >
                        {/* Capa base: en web -> play/pause; en mobile -> reels/reacciones */}
                        <TouchableOpacity
                            style={{
                                ...StyleSheet.absoluteFillObject,
                                zIndex: 10000,
                                ...(Platform.OS !== 'web' && DEBUG_GESTURES
                                    ? {
                                          backgroundColor: 'rgba(255,0,0,0.12)',
                                          borderWidth: 1,
                                          borderColor: 'rgba(255,0,0,0.6)',
                                      }
                                    : null),
                            }}
                            activeOpacity={1}
                            onPress={
                                Platform.OS === 'web'
                                    ? handlePlayPause
                                    : handleBasePress
                            }
                            onLongPress={
                                Platform.OS !== 'web' ? handleLongPress : undefined
                            }
                            delayLongPress={650}
                        />
                        {(() => {
                            const pausedOverlay =
                                Platform.OS !== 'web' &&
                                controlsMode === 'post' &&
                                status?.isLoaded &&
                                !status?.isPlaying &&
                                !endedRef.current
                            const showCenter = showCustomControls || pausedOverlay
                            if (!showCenter) return null
                            return (
                                <View
                                    pointerEvents='box-none'
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 10001,
                                        opacity: 1,
                                    }}
                                >
                                    <View pointerEvents='auto'>
                                        <TouchableOpacity
                                            onPress={handlePlayPause}
                                            activeOpacity={0.9}
                                            style={{
                                                backgroundColor: 'rgba(0,0,0,0.45)',
                                                padding: 18,
                                                borderRadius: 48,
                                            }}
                                        >
                                            {status?.isPlaying ? (
                                                <Feather
                                                    name='pause'
                                                    size={36}
                                                    color='white'
                                                />
                                            ) : (
                                                <Feather
                                                    name='play'
                                                    size={36}
                                                    color='white'
                                                />
                                            )}
                                        </TouchableOpacity>
                                    </View>
                                </View>
                            )
                        })()}
                        {/* Botón de sonido siempre visible */}
                        <Animated.View
                            pointerEvents='box-none'
                            style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                zIndex: 10002,
                                opacity: 0.2,
                                transform: [{ scale: muteScale }],
                            }}
                        >
                            <TouchableOpacity
                                style={{
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    padding: 12,
                                    borderRadius: 25,
                                    ...(DEBUG_GESTURES
                                        ? {
                                              borderWidth: 1,
                                              borderColor: 'rgba(0,255,0,0.8)',
                                          }
                                        : null),
                                }}
                                onPress={handleMuteToggle}
                                onPressIn={onMutePressIn}
                                onPressOut={onMutePressOut}
                                activeOpacity={0.8}
                            >
                                <Feather
                                    name={isMuted ? 'volume-x' : 'volume-2'}
                                    size={20}
                                    color='white'
                                />
                            </TouchableOpacity>
                        </Animated.View>
                        {/* Overlay de "Volver a ver" al finalizar (solo si no es gestionado externamente) */}
                        {!manageReplayOverlayExternally && showReplayOverlay && (
                            <View
                                style={{
                                    ...StyleSheet.absoluteFillObject,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                pointerEvents='box-none'
                            >
                                <TouchableOpacity
                                    onPress={handleReplay}
                                    activeOpacity={0.9}
                                    style={{
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                        paddingVertical: 12,
                                        paddingHorizontal: 16,
                                        borderRadius: 28,
                                    }}
                                >
                                    <Feather
                                        name='rotate-ccw'
                                        size={20}
                                        color='#fff'
                                    />
                                    <Text
                                        style={{
                                            color: '#fff',
                                            marginLeft: 8,
                                            fontWeight: '600',
                                        }}
                                    >
                                        Volver a ver
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        )}
                    </View>
                )}
            </View>
        )
    }

    const rootWidthStyle = { width: '100%' }
    return (
        <View
            style={[
                isPodcast ? styles.containerPodcast : styles.container,
                rootWidthStyle,
            ]}
        >
            {isPodcast && (
                <ImageBackground
                    source={{ uri: podcastBackground }}
                    style={
                        isPodcast
                            ? styles.backgroundImagePodcast
                            : styles.backgroundImage
                    }
                    pointerEvents='none'
                >
                    {isAndroid() && (
                        <View
                            style={{
                                flexDirection: 'row',
                                alignItems: 'baseline',
                                margin: 12,
                            }}
                        >
                            <Feather name='mic' size={24} color={'white'} />
                            <Text style={styles.titlePodcast}>PODCAST</Text>
                        </View>
                    )}
                </ImageBackground>
            )}
            {renderVideoElement()}
            {!isWebPlatform() && showOfflineModal && <NoConnectionVideo />}
        </View>
    )
}

// Usar React.memo para evitar re-renders innecesarios
// Comparar props críticos para determinar si debe re-renderizar
export default React.memo(VideoPlayerAV, (prevProps, nextProps) => {
    // Siempre re-renderizar si cambia la URI (nuevo video)
    if (prevProps.uri !== nextProps.uri) return false

    // Re-renderizar si cambian props de control críticos
    if (prevProps.isVisible !== nextProps.isVisible) return false
    if (prevProps.autoplay !== nextProps.autoplay) return false
    if (prevProps.externalShouldPlay !== nextProps.externalShouldPlay) return false
    if (prevProps.containerWidth !== nextProps.containerWidth) return false

    // Para el resto de props, evitar re-renders innecesarios
    return true
})
